// ── Главная: «на сегодня», «Продолжить», очередь и библиотека ──────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PopMenu, anchorOf } from "../Chrome";
import { LangMigrationBanner } from "../components/Lang";
import { LeechNudge } from "../games/GamesEntry";
import { planMins, readStreak, useTodayPlan } from "../components/Today";
import { useOneShot } from "../hooks";
import { t } from "../i18n";
import Icon from "../Icon";
import { fmtClock, lastPlayed, readPlayPos } from "../playPos";
import { markSetting } from "../settings";
import { SANS, cardColorIn, themeKit } from "../themes";
import { Chip, ListRow, notify, IconButton as UiIconButton, Button as UiButton } from "../ui";
import DueCount from "../ui/DueCount";
import CountUp from "../ui/CountUp";
import { countWords, dateStr, nCards, nWords, ruPlural } from "../utils";
import { useWordStore } from "../wordLearning";
import { useMedia, usePalette } from "../ui/theme";
import { fieldSurface, panelSurface } from '../ui/surface';
import WordSlots from '../components/WordSlots';
import HandHome, { HandGreeting } from './HandHome';

// ── VocabQuiz — квиз по словарю из TextReader ────────────────────────────────
// ── Small button on a text-project card that reads learning words from store ──
// ── Экспорт карточек в текстовый файл (подходит для импорта в Anki) ──────────

export function exportAnkiTsv(project) {
  const cards = project.cards || [];
  if (!cards.length) return;
  const esc = (s) => (s||'').replace(/\t/g,' ').replace(/\n/g,' ');
  const lines = [
    '#separator:Tab',
    '#html:false',
    '#notetype:Basic',
    `#deck:AntonCards - ${esc(project.name)}`,
    'Front\tBack\tTags',
    ...cards.map(c=>`${esc(c.word)}\t${esc(c.translation)}\tantonCards`),
  ];
  const blob = new Blob([lines.join('\n')], {type:'text/plain;charset=utf-8'});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `${(project.name||'deck').replace(/[^a-zA-Zа-яА-Я0-9_-]/g,'_')}_anki.txt`;
  a.click();
  setTimeout(()=>URL.revokeObjectURL(url), 5000);
}

// ── Главная: сводка на сегодня, «Продолжить» и библиотека (макет «вариант A») ──
export function ProjectsScreen({slotWords,userName,email,navMode=false,newFolderReq=0,projects:rawProjects,extraFolders,onNew,onNewMenu,onOpen,onPlayHere,playingId=null,announcement=null,onDismissAnnouncement,onDelete,onRename,onMoveToFolder,onCreateFolder,onDeleteFolder,theme,loading,syncResult,syncErrorDetail='',onQuiz,textProjects:rawTextProjects=[],onGames,onOpenTextProject,onDeleteTextProject,onRenameTextProject,onMoveTextToFolder,onNewTextProject,onImportBook,onLinkTextToAudio,downloadStatus,onDownload,onDeleteOffline,onBulkDownload,activeLang,onAssignLang,activeFolder,onActiveFolder,searchQuery,onSearchQuery,queue=[],onQueueAdd,onQueueNext,onQueueRemove,onOpenQueue}){
  // Пиявки считаем по карточкам изучаемого языка (как и всё на главной).
  const leechCards=useMemo(()=>(rawProjects||[]).filter(p=>!activeLang||(p.src_lang||"").toLowerCase()===activeLang).flatMap(p=>p.cards||[]),[rawProjects,activeLang]);
  const C=usePalette();
  const store=useWordStore();
  // Стрик пересчитывается при любом recordActivity (в этой или соседней вкладке)
  // и при возврате в окно (за ночь мог смениться день).
  const [streakInfo,setStreakInfo]=useState(readStreak);
  useEffect(()=>{
    const refresh=()=>setStreakInfo(readStreak());
    const onStorage=e=>{if(!e.key||e.key==="ac_activity_by_lang")refresh();};
    window.addEventListener("ac:activity-changed",refresh);
    window.addEventListener("storage",onStorage);
    window.addEventListener("focus",refresh);
    return()=>{
      window.removeEventListener("ac:activity-changed",refresh);
      window.removeEventListener("storage",onStorage);
      window.removeEventListener("focus",refresh);
    };
  },[]);
  // Выбран язык — показываем только его проекты и тексты; «все языки» — всё.
  const matchLang=useCallback((srcLang)=>!activeLang||(srcLang||'').toLowerCase()===activeLang,[activeLang]);
  const projects     = useMemo(()=>rawProjects.filter(p=>matchLang(p.src_lang)),[rawProjects,matchLang]);
  const textProjects = useMemo(()=>rawTextProjects.filter(p=>matchLang(p.src_lang)),[rawTextProjects,matchLang]);
  const [bulkDl,setBulkDl]=useState({active:false,done:0,total:0,failed:0});
  // Папка и поиск живут в адресе (их хранит App): «Назад» браузера и телефона
  // переходит между ними как между страницами.
  const setActiveFolder=onActiveFolder;
  const [renaming,setRenaming]=useState(null); // {kind:'proj'|'text'|'folder', id}
  const [renameVal,setRenameVal]=useState("");
  const [newFolderName,setNewFolderName]=useState("");
  const [showNewFolder,setShowNewFolder]=useState(false);
  useOneShot("folder",newFolderReq,()=>{setShowNewFolder(true);window.scrollTo({top:0,behavior:"smooth"});});
  const [dragOver,setDragOver]=useState(null);
  const [sortBy,setSortBy]=useState(()=>{try{return localStorage.getItem("ac_sort_by")||"created_desc";}catch{return"created_desc";}});
  useEffect(()=>{try{localStorage.setItem("ac_sort_by",sortBy);markSetting("ac_sort_by");}catch{};},[sortBy]);
  const [linkPick,setLinkPick]=useState(null); // текст, который привязываем к аудио (на телефоне нет перетаскивания)
  const [menu,setMenu]=useState(null); // {title, items, at|sheet}
  const projFolders=Array.from(new Set(projects.map(p=>p.folder||"").filter(f=>f!==""))).sort();
  const folders=Array.from(new Set([...projFolders,...extraFolders])).sort();

  // ─── Поиск по всем карточкам ─────────────────────────────────────────────
  const searchResults=useMemo(()=>{
    const q=searchQuery.trim().toLowerCase();
    if(!q)return[];
    const results=[];
    projects.forEach(proj=>(proj.cards||[]).forEach(card=>{
      if((card.word||"").toLowerCase().includes(q)||(card.translation||"").toLowerCase().includes(q))results.push({card,proj});
    }));
    return results.slice(0,200);
  },[searchQuery,projects]);
  const hasSearch=searchQuery.trim().length>0;

  const ungrouped=projects.filter(p=>!p.folder);
  const inF=(f)=>projects.filter(p=>p.folder===f);
  const textsIn=(f)=>textProjects.filter(tp=>(tp.folder||"")===f);
  const sortProjects=(list)=>{
    const copy=[...list];
    if(sortBy==="name_asc")return copy.sort((a,b)=>(a.name||"").localeCompare(b.name||"",undefined,{numeric:true,sensitivity:"base"}));
    if(sortBy==="updated_desc")return copy.sort((a,b)=>new Date(b.updated_at||0)-new Date(a.updated_at||0));
    return copy.sort((a,b)=>new Date(b.created_at||b.updated_at||0)-new Date(a.created_at||a.updated_at||0));
  };
  // Аудио и тексты — одним списком в выбранном порядке (папки всегда сверху).
  const sortedRows=(audio,texts,tile=false)=>sortProjects([...audio.map(p=>({...p,_kind:"a"})),...texts.map(t=>({...t,_kind:"t"}))])
    .map(x=>x._kind==="a"?projRow(audio.find(p=>p.id===x.id),tile):textRow(texts.find(t=>t.id===x.id),tile));
  const createFolder=()=>{const n=newFolderName.trim();if(!n)return;onCreateFolder(n);setShowNewFolder(false);setNewFolderName("");setActiveFolder(n);};
  const renameFolder=(oldName,newName)=>{
    const n=newName.trim();if(!n||n===oldName)return;
    inF(oldName).forEach(p=>onMoveToFolder(p.id,n));
    textsIn(oldName).forEach(tp=>onMoveTextToFolder(tp.id,n));
    onCreateFolder(n);onDeleteFolder(oldName);
    if(activeFolder===oldName)setActiveFolder(n);
  };
  const startRename=(kind,id,name)=>{setRenaming({kind,id});setRenameVal(name||"");};
  const commitRename=()=>{
    const r=renaming;const v=renameVal.trim();setRenaming(null);
    if(!r||!v)return;
    if(r.kind==="proj")onRename(r.id,v);
    else if(r.kind==="text")onRenameTextProject(r.id,v);
    else renameFolder(r.id,v);
  };

  // ── Сводка на сегодня ─────────────────────────────────────────────────────
  const allCards=useMemo(()=>projects.flatMap(p=>p.cards||[]),[projects]);
  const plan=useTodayPlan(allCards);
  const dueToday=plan.total;
  const last=useMemo(()=>lastPlayed(projects),[projects]);

  // ── Меню «⋯»: на компьютере рядом с кнопкой, на телефоне — лист снизу ─────
  const lastAt=useRef(null);
  const openMenu=(e,title,items)=>{
    e.stopPropagation();
    if(navMode){setMenu({title,items,sheet:true});return;}
    lastAt.current=anchorOf(e.currentTarget,250);
    setMenu({title,items,at:lastAt.current});
  };
  // Второе меню (выбор папки) открывается на месте первого.
  const reopenMenu=(title,items)=>setMenu(navMode?{title,items,sheet:true}:{title,items,at:lastAt.current});
  const pickFolder=(current,move)=>reopenMenu(t("Переместить в папку"),[
    {icon:"home",label:t("Без папки"),checked:!current,onClick:()=>move("")},
    ...folders.map(f=>({icon:"folder",label:f,checked:current===f,onClick:()=>move(f)})),
  ]);

  const projItems=(p)=>{
    const n=p.cardCount||0;
    const linkedText=rawTextProjects.find(tp=>tp.linked_audio_id===p.id);
    const dl=downloadStatus[p.id];
    return[
      {icon:"play",label:t("Слушать"),onClick:()=>onOpen(p)},
      ...(onQueueAdd&&(p.audio_path||p.local_audio_filename)?(queue.includes(String(p.id))
        ?[{icon:"list",label:t("Убрать из очереди"),hint:t("Сейчас №{0} в очереди",[queue.indexOf(String(p.id))+1]),onClick:()=>onQueueRemove(p)}]
        :[{icon:"next",label:t("Играть следующим"),hint:t("Сразу после текущего аудио"),onClick:()=>onQueueNext(p)},
          {icon:"list",label:t("Добавить в очередь"),hint:t("В конец очереди"),onClick:()=>onQueueAdd(p)}]):[]),
      n>0&&{icon:"cards",label:t("Повторить карточки"),hint:t("Только этот проект"),onClick:()=>onQuiz(p.id)},
      linkedText&&{icon:"book",label:t("Открыть текст"),hint:linkedText.name,onClick:()=>onOpenTextProject(linkedText)},
      "divider",
      {icon:"edit",label:t("Переименовать"),onClick:()=>startRename("proj",p.id,p.name)},
      (folders.length>0||p.folder)&&{icon:"folder",label:t("Переместить в папку"),onClick:()=>pickFolder(p.folder||"",f=>onMoveToFolder(p.id,f))},
      p.audio_path&&(dl==="done"
        ?{icon:"trash",label:t("Удалить офлайн-копию"),hint:t("Аудио останется в облаке"),onClick:()=>onDeleteOffline?.(p)}
        :typeof dl!=="number"&&{icon:"download",label:t("Скачать для офлайна"),hint:t("Слушать без интернета"),onClick:()=>onDownload(p)}),
      n>0&&"divider",
      n>0&&{icon:"download",label:t("Скачать карточки (.txt)"),hint:t("Для Anki и других программ"),onClick:()=>exportAnkiTsv(p)},
      "divider",
      {icon:"trash",label:t("Удалить"),danger:true,onClick:()=>onDelete(p.id)},
    ];
  };
  const textItems=(tp)=>[
    {icon:"book",label:t("Читать"),onClick:()=>onOpenTextProject(tp)},
    "divider",
    {icon:"edit",label:t("Переименовать"),onClick:()=>startRename("text",tp.id,tp.name)},
    {icon:"link",label:tp.linked_audio_id?t("Привязано к аудио — изменить"):t("Привязать к аудио"),onClick:()=>setLinkPick(tp)},
    (folders.length>0||tp.folder)&&{icon:"folder",label:t("Переместить в папку"),onClick:()=>pickFolder(tp.folder||"",f=>onMoveTextToFolder(tp.id,f))},
    "divider",
    {icon:"trash",label:t("Удалить"),danger:true,onClick:()=>onDeleteTextProject(tp.id)},
  ];
  const folderItems=(f)=>[
    {icon:"edit",label:t("Переименовать"),onClick:()=>startRename("folder",f,f)},
    {icon:"trash",label:t("Удалить папку"),hint:t("Проекты из неё останутся в библиотеке"),danger:true,onClick:()=>onDeleteFolder(f)},
  ];

  // ── Строка списка (как на макете): значок, название, подробности, «⋯» ─────
  const renameField=(
    <span style={{display:"flex",gap:6,alignItems:"center"}} onClick={e=>e.stopPropagation()}>
      <input autoFocus value={renameVal} onChange={e=>setRenameVal(e.target.value)} aria-label={t("Новое название")}
        onKeyDown={e=>{if(e.key==="Enter")commitRename();if(e.key==="Escape")setRenaming(null);}}
        style={{flex:1,minWidth:0,height:36,fontFamily:SANS,fontSize:15,color:C.text,...fieldSurface(C),borderColor:C.blue,borderRadius:8,padding:"0 10px",outline:"none"}}/>
      <UiIconButton variant="filled" size={36} label={t("Сохранить")} onClick={commitRename}><Icon n="check" s={16}/></UiIconButton>
      <button onClick={()=>setRenaming(null)} aria-label={t("Отмена")} className="ac-ibtn" style={{width:36,height:36,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:C.lift?C.surface:"none",border:`1px solid ${C.border}`,borderRadius:8,color:C.mid,cursor:"pointer"}}><Icon n="close" s={14}/></button>
    </span>
  );
  // «Колода» (layout hand): библиотека — плитками в две колонки, как на макете.
  const hand=themeKit(C).layout==="hand";
  const tileOf=({key,icon,tint,title,meta,right,open,items,menuTitle,isRenaming,drag,highlight,dot})=>(
    <div key={key} role="button" tabIndex={0} data-panel="" className="ac-row-click" {...drag}
      onClick={isRenaming?undefined:open} onKeyDown={e=>{if(e.key==="Enter"&&!isRenaming){e.preventDefault();open();}}}
      onContextMenu={items?(e=>{e.preventDefault();openMenu(e,menuTitle,items());}):undefined}
      style={{...panelSurface(C),borderRadius:10,padding:"14px 40px 14px 14px",display:"flex",alignItems:"center",gap:12,minHeight:76,cursor:"pointer",
        position:"relative",boxSizing:"border-box",minWidth:0,outline:highlight?`2px solid ${C.blue}`:undefined}}>
      <span style={{position:"relative",width:42,height:42,borderRadius:5,flexShrink:0,background:C.face?C.face.bg:C.surface,color:C.face?C.face.text:(tint||C.mid),
        display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
        <Icon n={icon} s={20}/>
        {dot&&<span title={t("Скачано: работает без интернета")} style={{position:"absolute",right:-3,bottom:-3,width:11,height:11,borderRadius:"50%",background:C.green,border:`2px solid ${C.card}`}}/>}
      </span>
      <span style={{flex:1,minWidth:0}}>
        {isRenaming?renameField:<>
          <span style={{display:"block",fontSize:14.5,fontWeight:500,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{title}</span>
          {meta&&<span style={{display:"block",fontSize:12,color:C.faint,marginTop:2,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{meta}</span>}
        </>}
      </span>
      {right}
      <span aria-hidden="true" style={{position:"absolute",top:10,right:12,color:C.faint,display:"inline-flex"}}><Icon n="arrowUpRight" s={14}/></span>
      {items&&!isRenaming&&<span style={{position:"absolute",right:4,bottom:4}}>
        <UiIconButton icon="more" iconSize={18} size={32} variant="ghost" label={t("Действия: {0}",[menuTitle])} title={t("Действия")} aria-haspopup="menu" onClick={e=>openMenu(e,menuTitle,items())}/>
      </span>}
    </div>
  );
  const row=(args,tile=false)=>tile?tileOf(args):listRow(args);
  const listRow=({key,icon,tint,title,meta,right,open,items,menuTitle,isRenaming,drag,highlight,dot})=>(
    <ListRow key={key} highlight={highlight} {...drag}
      onClick={isRenaming?undefined:open}
      onContextMenu={items?(e=>{e.preventDefault();openMenu(e,menuTitle,items());}):undefined}
      leading={<span style={{position:"relative",width:38,height:38,borderRadius:9,background:C.surface,color:tint||C.mid,display:"inline-flex",alignItems:"center",justifyContent:"center"}}>
        <Icon n={icon} s={19}/>
        {dot&&<span title={t("Скачано: работает без интернета")} style={{position:"absolute",right:-3,bottom:-3,width:12,height:12,borderRadius:"50%",background:C.green,border:`2px solid ${C.card}`}}/>}
      </span>}
      title={title} subtitle={meta}
      trailing={!isRenaming&&(right||items)&&<>
        {right}
        {items&&<UiIconButton icon="more" iconSize={20} variant="ghost" label={t("Действия: {0}",[menuTitle])} title={t("Действия")} aria-haspopup="menu" onClick={e=>openMenu(e,menuTitle,items())}/>}
      </>}>
      {isRenaming?<span style={{flex:1,minWidth:0}}>{renameField}</span>:undefined}
    </ListRow>
  );
  const folderRow=(f,tile=false)=>{
    const nA=inF(f).length,nT=textsIn(f).length;
    const parts=[];if(nA)parts.push(t("{0} аудио",[nA]));if(nT)parts.push(`${nT} ${ruPlural(nT,"текст","текста","текстов")}`);
    return row({key:`f:${f}`,icon:"folder",tint:C.mid,title:f,meta:parts.length?parts.join(" · "):t("пусто"),
      open:()=>setActiveFolder(f),items:()=>folderItems(f),menuTitle:f,isRenaming:renaming?.kind==="folder"&&renaming.id===f,
      highlight:dragOver===f,
      drag:{onDragOver:e=>{e.preventDefault();setDragOver(f);},onDragLeave:()=>setDragOver(null),
        onDrop:e=>{e.preventDefault();setDragOver(null);const id=e.dataTransfer.getData("projId");const textId=e.dataTransfer.getData("textProjId");if(id)onMoveToFolder(id,f);if(textId)onMoveTextToFolder(textId,f);}}},tile);
  };
  // «Очередь» над списком (на главной и в папке): открыть, сколько в ней.
  const queueChip=onOpenQueue&&rawProjects.length>1&&(
    <UiButton size="sm" icon="list" onClick={onOpenQueue} title={t("Очередь: аудио одно за другим")}>
      {t("Очередь")}{queue.length>0&&<span style={{color:C.blueDark,fontVariantNumeric:"tabular-nums",marginLeft:6}}>{queue.length}</span>}
    </UiButton>
  );
  const projRow=(p,tile=false)=>{
    const dl=downloadStatus[p.id];
    const pos=readPlayPos(p.id);
    const linkedText=rawTextProjects.find(tp=>tp.linked_audio_id===p.id);
    const qi=queue.indexOf(String(p.id));
    const meta=[qi>=0?t("в очереди: {0}",[qi+1]):null,nCards(p.cardCount||0),pos>0?t("остановились на {0}",[fmtClock(pos)]):null,linkedText?t("есть текст"):null,!navMode?dateStr(p.updated_at||p.created_at):null].filter(Boolean).join(" · ");
    return row({key:p.id,icon:"headphones",tint:C.blueDark,title:p.name,meta,open:()=>onOpen(p),items:()=>projItems(p),menuTitle:p.name,
      isRenaming:renaming?.kind==="proj"&&renaming.id===p.id,dot:dl==="done",highlight:dragOver===`p:${p.id}`,
      right:typeof dl==="number"&&dl<100?<span style={{fontSize:12.5,fontWeight:600,color:C.blueDark,fontVariantNumeric:"tabular-nums",flexShrink:0}}>{dl}%</span>
        :dl==="error"?<UiIconButton icon="alert" iconSize={18} variant="ghost" danger label={t("Повторить скачивание")} title={t("Не скачалось — повторить")} onClick={()=>onDownload(p)}/>:null,
      drag:{draggable:true,onDragStart:e=>e.dataTransfer.setData("projId",p.id),
        onDragOver:e=>{if(e.dataTransfer.types.includes("textprojid")){e.preventDefault();setDragOver(`p:${p.id}`);}},
        onDragLeave:e=>{if(!e.currentTarget.contains(e.relatedTarget))setDragOver(null);},
        onDrop:e=>{const textId=e.dataTransfer.getData("textProjId");setDragOver(null);if(textId){e.preventDefault();onLinkTextToAudio?.(textId,p.id);}}}},tile);
  };
  const textRow=(tp,tile=false)=>{
    const words=tp.content?countWords(tp.content,tp.src_lang):0;
    const meta=[nWords(words),tp.linked_audio_id?t("с аудио"):null,!navMode?dateStr(tp.updated_at||tp.created_at):null].filter(Boolean).join(" · ");
    return row({key:tp.id,icon:"book",tint:C.green,title:tp.name||t("Без названия"),meta,open:()=>onOpenTextProject(tp),items:()=>textItems(tp),menuTitle:tp.name||t("Текст"),
      isRenaming:renaming?.kind==="text"&&renaming.id===tp.id,
      drag:{draggable:true,onDragStart:e=>e.dataTransfer.setData("textProjId",tp.id)}},tile);
  };

  // Привязать текст к аудио — выбор из списка (работает и пальцем, в отличие от перетаскивания)
  const linkPickerEl=linkPick&&(()=>{
    const lang=(linkPick.src_lang||"").toLowerCase();
    const list=[...(rawProjects||[])].sort((a,b)=>{
      const sa=(a.src_lang||"").toLowerCase()===lang?0:1,sb=(b.src_lang||"").toLowerCase()===lang?0:1;
      return sa-sb||(a.name||"").localeCompare(b.name||"");
    });
    return(
      <PopMenu C={C} sheet={navMode} at={navMode?undefined:{top:"12vh",left:"calc(50% - 180px + var(--ac-side-w, 0px) / 2)"}} width={360}
        title={t("Привязать «{0}» к аудио",[linkPick.name])} onClose={()=>setLinkPick(null)}
        items={[
          ...list.map(p=>({icon:"headphones",label:p.name,hint:p.src_lang||undefined,checked:p.id===linkPick.linked_audio_id,onClick:()=>onLinkTextToAudio?.(linkPick.id,p.id)})),
          !list.length&&{header:t("Аудиопроектов пока нет.")},
          linkPick.linked_audio_id&&"divider",
          linkPick.linked_audio_id&&{icon:"close",label:t("Отвязать от аудио"),danger:true,onClick:()=>onLinkTextToAudio?.(linkPick.id,null)},
        ]}/>
    );
  })();

  const listBox={...panelSurface(C),borderRadius:12,overflow:"hidden"};
  // Широкий экран (боковое меню + больше ~1170 px на содержимое): страница шире, библиотека —
  // сеткой в две колонки, чтобы по бокам не оставалось пустоты (разделители — .ac-list-2 в index.css).
  const wide=useMedia("(min-width: 1400px)")&&!navMode;
  const libBox=wide?{...listBox,display:"grid",gridTemplateColumns:"repeat(2, minmax(0, 1fr))"}:listBox;
  const libClass=wide?"ac-list ac-list-2":"ac-list";
  const label={fontSize:13,fontWeight:600,color:C.mid,margin:"0 0 8px"};
  const page={maxWidth:wide?1180:880,margin:"0 auto",padding:navMode?"12px 16px 0":"20px 24px 0",boxSizing:"border-box",display:"flex",flexDirection:"column",gap:navMode?12:16,
    paddingBottom:"calc(88px + var(--ac-mini-h, 0px) + var(--ac-nav-h, 0px) + env(safe-area-inset-bottom, 0px))"};
  const sortBar=(
    <div role="group" aria-label={t("Сортировка")} style={{display:"flex",gap:4,alignItems:"center"}}>
      {[["created_desc",t("Новые")],["updated_desc",t("Изменённые")],["name_asc",t("А–Я")]].map(([v,l])=>(
        <Chip key={v} selected={sortBy===v} onClick={()=>setSortBy(v)}>{l}</Chip>
      ))}
    </div>
  );
  const styles=<style>{`@keyframes shimmer{0%{background-position:100% 0}100%{background-position:-200% 0}}`}</style>;
  const menuEl=menu&&<PopMenu C={C} sheet={menu.sheet} at={menu.at} title={menu.title} items={menu.items} width={250} onClose={()=>setMenu(null)}/>;
  const extras=(<>
    {styles}{menuEl}{linkPickerEl}
  </>);
  const skeleton=(
    <div data-panel="" style={listBox}>{[1,2,3].map(i=>(
      <div key={i} style={{display:"flex",alignItems:"center",gap:12,height:60,padding:"0 12px",borderTop:i>1?`1px solid ${C.borderSoft}`:"none"}}>
        <span style={{width:38,height:38,borderRadius:9,background:C.surface}}/>
        <span style={{flex:1}}><span style={{display:"block",height:12,width:"45%",borderRadius:6,background:`linear-gradient(90deg,${C.surface} 0%,${C.border} 50%,${C.surface} 100%)`,backgroundSize:"300% 100%",animation:"shimmer 1.4s ease-in-out infinite"}}/></span>
      </div>
    ))}</div>
  );

  // ── Открытая папка ────────────────────────────────────────────────────────
  if(activeFolder!==null){
    const fps=sortProjects(inF(activeFolder));
    const fts=textsIn(activeFolder);
    const dlable=fps.filter(p=>p.audio_path&&downloadStatus[p.id]!=="done"&&typeof downloadStatus[p.id]!=="number");
    const busy=bulkDl.active;
    const bulk=()=>{
      if(busy)return;
      setBulkDl({active:true,done:0,total:dlable.length,failed:0});
      onBulkDownload(fps,({done,total,failed,finished})=>{
        setBulkDl({active:!finished,done,total,failed});
        if(finished&&total>0&&failed>0)notify(t("Скачано {0} из {1}. Не удалось: {2}.",[done,total,failed]),{tone:"error"});
      });
    };
    return(
      <div style={{minHeight:"100vh",background:C.backdrop,fontFamily:SANS}}>
        <div style={page}>
          <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            {!navMode&&(
              <div style={{display:"flex",alignItems:"center",gap:8,flex:"1 1 260px",minWidth:0}}>
                <button data-btn="" onClick={()=>setActiveFolder(null)} className="ac-ibtn" title={t("Назад, на главную")} aria-label={t("Назад, на главную")}
                  onDragOver={e=>{e.preventDefault();e.currentTarget.style.background=C.bluePale;}}
                  onDragLeave={e=>{e.currentTarget.style.background="";}}
                  onDrop={e=>{e.preventDefault();e.currentTarget.style.background="";const id=e.dataTransfer.getData("projId");const textId=e.dataTransfer.getData("textProjId");if(id)onMoveToFolder(id,"");if(textId)onMoveTextToFolder(textId,"");}}
                  style={{width:40,height:40,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:C.lift?C.surface:"none",border:`1px solid ${C.border}`,borderRadius:8,color:C.mid,cursor:"pointer"}}><Icon n="arrowLeft" s={18}/></button>
                <h1 style={{margin:0,fontSize:22,fontWeight:600,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",letterSpacing:"-0.01em"}}>{activeFolder}</h1>
              </div>
            )}
            <div style={{display:"flex",gap:8,alignItems:"center",marginLeft:navMode?0:"auto",width:navMode?"100%":undefined}}>
              {dlable.length>0&&(
                <UiButton icon="download" onClick={bulk} disabled={busy} style={{flex:navMode?1:undefined}}
                  title={t("Скачать все аудио папки, чтобы слушать без интернета")}>
                  {busy?t("{0} из {1}",[bulkDl.done,bulkDl.total]):t("Скачать всё")}
                </UiButton>
              )}
              <UiButton variant="primary" icon="plus" onClick={e=>onNewMenu(e,activeFolder)} style={{flex:navMode?1:undefined,justifyContent:"center"}}>{t("Добавить")}</UiButton>
            </div>
          </div>
          {loading&&!fps.length?skeleton:(!fps.length&&!fts.length)?(
            <div style={{...listBox,textAlign:"center",padding:"40px 16px"}}>
              <div style={{color:C.faint,marginBottom:10}}><Icon n="folder" s={32} sw={1.5}/></div>
              <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:6}}>{t("Папка пуста")}</div>
              <div style={{fontSize:14,color:C.mid,marginBottom:16}}>{t("Добавьте аудио или текст — или перетащите их сюда с главной.")}</div>
              <UiButton variant="primary" icon="plus" onClick={()=>onNew(activeFolder)}>{t("Аудио проект")}</UiButton>
            </div>
          ):(
            <div>
              {(fps.length+fts.length>1||(queueChip&&fps.length>0))&&(
                <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap",marginBottom:8}}>
                  {fps.length>0?queueChip:<span/>}
                  {fps.length+fts.length>1&&sortBar}
                </div>
              )}
              <div className={libClass} data-panel="" style={libBox}>{sortedRows(fps,fts)}</div>
            </div>
          )}
        </div>
        {extras}
      </div>
    );
  }

  // ── Главная ───────────────────────────────────────────────────────────────
  const rootTexts=textProjects.filter(tp=>!tp.folder);
  const isEmpty=!projects.length&&!folders.length&&!rootTexts.length;
  const streak=streakInfo.count;
  const tile={...listBox,padding:"14px 16px",display:"flex",flexDirection:"column",gap:4,minWidth:0};
  const tileLabel={fontSize:13,color:C.mid,display:"flex",alignItems:"center",gap:6};
  const tileValue={fontSize:24,fontWeight:600,color:C.text,lineHeight:1.2,fontVariantNumeric:"tabular-nums",letterSpacing:"-0.01em"};
  const planPct=plan.all?Math.round(plan.done/plan.all*100):0;
  const planDone=plan.total===0&&plan.done>0;
  return(
    <div style={{minHeight:"100vh",background:C.backdrop,fontFamily:SANS}}>
      <div style={page}>
        {hand&&!hasSearch&&<HandGreeting C={C} navMode={navMode} userName={userName} email={email} activeLang={activeLang}/>}
        {navMode&&syncResult==="error"&&(
          <div role="status" style={{fontSize:13,color:C.text,background:C.warmPale,border:`1px solid ${C.border}`,borderRadius:10,padding:"10px 12px",lineHeight:1.45}}>
            {t("Синхронизация не удалась")}{syncErrorDetail?`: ${syncErrorDetail}`:""}{t(". Изменения сохранены на устройстве и отправятся сами, когда появится связь.")}
          </div>
        )}
        {announcement&&(
          <div role="status" style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 8px 10px 14px",borderRadius:10,fontSize:14,lineHeight:1.45,
            background:announcement.level==="warn"?C.warmPale:C.bluePale,color:C.text,border:`1px solid ${announcement.level==="warn"?C.warm:C.border}`}}>
            <span style={{color:announcement.level==="warn"?C.warmDark:C.blueDark,display:"inline-flex",marginTop:2}}><Icon n="alert" s={16}/></span>
            <span style={{flex:1,minWidth:0,whiteSpace:"pre-wrap"}}>{announcement.text}</span>
            <button onClick={onDismissAnnouncement} aria-label={t("Закрыть объявление")} className="ac-ibtn"
              style={{width:32,height:32,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:"none",border:"none",borderRadius:8,color:C.mid,cursor:"pointer"}}><Icon n="close" s={14}/></button>
          </div>
        )}
        <LangMigrationBanner projects={rawProjects} textProjects={rawTextProjects} onAssignLang={onAssignLang} activeLang={activeLang}/>
        <LeechNudge cards={leechCards} onGames={onGames}/>
        {showNewFolder&&(
          <div style={{...listBox,padding:"10px 12px",display:"flex",gap:8,alignItems:"center"}}>
            <span style={{color:C.mid,display:"inline-flex"}}><Icon n="folder" s={18}/></span>
            <input autoFocus value={newFolderName} onChange={e=>setNewFolderName(e.target.value)} placeholder={t("Название папки")} aria-label={t("Название папки")}
              onKeyDown={e=>{if(e.key==="Enter")createFolder();if(e.key==="Escape"){setShowNewFolder(false);setNewFolderName("");}}}
              style={{flex:1,minWidth:0,height:40,...fieldSurface(C),borderRadius:8,color:C.text,padding:"0 10px",fontSize:15,fontFamily:SANS,outline:"none"}}/>
            <UiButton variant="primary" onClick={createFolder}>{t("Создать")}</UiButton>
            <button onClick={()=>{setShowNewFolder(false);setNewFolderName("");}} aria-label={t("Отмена")} className="ac-ibtn"
              style={{width:40,height:40,flexShrink:0,display:"inline-flex",alignItems:"center",justifyContent:"center",background:C.lift?C.surface:"none",border:`1px solid ${C.border}`,borderRadius:8,color:C.mid,cursor:"pointer"}}><Icon n="close" s={14}/></button>
          </div>
        )}

        {hasSearch?(
          <div>
            <div style={label} role="status">{searchResults.length===0?t("Ничего не найдено"):t("Найдено: {0}",[searchResults.length===200?t("200+ карточек"):nCards(searchResults.length)])}</div>
            {searchResults.length===0?(
              <div style={{...listBox,textAlign:"center",padding:"36px 16px",color:C.mid,fontSize:14}}>
                <div style={{color:C.faint,marginBottom:10}}><Icon n="search" s={30} sw={1.5}/></div>
                {t("Нет карточек со словом «")}{searchQuery.trim()}»
              </div>
            ):(
              <div className="ac-list" data-panel="" style={listBox}>
                {searchResults.map(({card,proj},i)=>{
                  const q=searchQuery.trim().toLowerCase();
                  const hl=(str)=>{
                    if(!str)return str;
                    const idx=str.toLowerCase().indexOf(q);
                    if(idx<0)return str;
                    return <>{str.slice(0,idx)}<mark style={{background:C.bluePale,color:C.blueDark,borderRadius:3,padding:"0 1px"}}>{str.slice(idx,idx+q.length)}</mark>{str.slice(idx+q.length)}</>;
                  };
                  return(
                    <ListRow key={`${proj.id}-${card.id}-${i}`} onClick={()=>onOpen(proj,card.time_sec)} style={{minHeight:56}}
                      leading={<span style={{width:8,height:8,borderRadius:"50%",background:cardColorIn(C,card.color)||C.border,display:"block"}}/>}>
                      <span style={{flex:1,minWidth:0,overflowWrap:"anywhere"}}>
                        <span style={{display:"block",fontSize:15,fontWeight:500,color:C.text}}>{hl(card.word)}</span>
                        {card.translation&&<span style={{display:"block",fontSize:13,color:C.mid,marginTop:1}}>{hl(card.translation)}</span>}
                      </span>
                      <span style={{flexShrink:0,textAlign:"right",maxWidth:"40%"}}>
                        <span style={{display:"block",fontSize:12.5,color:C.faint,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{proj.name}</span>
                        {card.time_sec!=null&&<span style={{display:"inline-flex",alignItems:"center",gap:4,fontSize:12.5,color:C.blueDark,fontWeight:600,fontVariantNumeric:"tabular-nums",marginTop:2}}><Icon n="play" s={10}/>{fmtClock(card.time_sec)}</span>}
                      </span>
                    </ListRow>
                  );
                })}
              </div>
            )}
          </div>
        ):hand?(
          <HandHome C={C} navMode={navMode} cards={allCards} plan={plan} last={last}
            playingId={playingId} onOpen={onOpen} onPlayHere={onPlayHere} onQuiz={onQuiz} onNewMenu={e=>onNewMenu?.(e,null)} slotWords={slotWords}
            chips={<>{queueChip}{ungrouped.length+rootTexts.length>1&&sortBar}</>}
            library={loading&&!projects.length&&!folders.length?skeleton:isEmpty?(
              <div data-panel="" style={{...listBox,textAlign:"center",padding:"32px 16px"}}>
                <div style={{fontSize:16,fontWeight:600,color:C.text,marginBottom:6}}>{t("Добро пожаловать!")}</div>
                <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap",marginTop:12}}>
                  <UiButton variant="primary" icon="headphones" onClick={()=>onNew(null)}>{t("Аудио проект")}</UiButton>
                  <UiButton icon="book" onClick={()=>onNewTextProject()}>{t("Текст")}</UiButton>
                </div>
              </div>
            ):(
              <div style={{display:"grid",gridTemplateColumns:navMode?"minmax(0,1fr)":"repeat(2,minmax(0,1fr))",gap:12}}>
                {folders.map(f=>folderRow(f,true))}
                {sortedRows(ungrouped,rootTexts,true)}
              </div>
            )}/>
        ):(
        <>
          {/* Сводка на сегодня */}
          <div style={{display:"grid",gap:navMode?10:12,gridTemplateColumns:navMode?"1fr 1fr":"1.4fr 1fr 1fr"}}>
            {/* data-foil: в «Аркаде» у главной плитки радужная голографическая рамка (index.css). */}
            <div data-foil="" data-tilt="" style={{...tile,gridColumn:navMode?"1 / -1":undefined}}>
              <div style={tileLabel}>{t("Повторить сегодня")}</div>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,flexWrap:"wrap"}}>
                <span style={{minWidth:0}}>
                  <span style={tileValue}><CountUp value={dueToday}/>{dueToday>0&&<span style={{fontSize:13,fontWeight:500,color:C.faint,marginLeft:8}}>{t("≈ {0} мин",[planMins(dueToday)])}</span>}</span>
                  {dueToday>0&&<span style={{display:"block",fontSize:12.5,color:C.mid,marginTop:2}}><DueCount C={C} repeat={plan.repeat} fresh={plan.fresh} variant="words"/></span>}
                </span>
                {dueToday>0
                  ?<UiButton variant="primary" icon="play" onClick={()=>onQuiz(null,true)}>{t("Начать")}</UiButton>
                  :<span style={{fontSize:13,color:C.green,display:"inline-flex",alignItems:"center",gap:6}}><Icon n="check" s={14}/>{t("На сегодня всё")}</span>}
              </div>
            </div>
            <div data-tilt="" data-panel="" style={tile} title={t("Сколько из плана на сегодня уже сделано")}>
              <div style={tileLabel}>{t("План на сегодня")}</div>
              <div style={tileValue}><CountUp value={plan.done}/> <span style={{color:C.faint,fontWeight:500}}>/ {plan.all}</span></div>
              <div role="progressbar" aria-label={t("План на сегодня")} aria-valuemin={0} aria-valuemax={plan.all} aria-valuenow={plan.done}
                style={{height:6,borderRadius:3,background:C.surface,marginTop:6,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${planDone?100:planPct}%`,borderRadius:3,background:planDone?C.green:C.blue,transition:"width .4s ease"}} data-meter=""/>
              </div>
            </div>
            <div data-tilt="" data-panel="" style={tile} title={streak>0&&!streakInfo.studiedToday?t("Позанимайтесь сегодня, чтобы не потерять серию"):undefined}>
              <div style={tileLabel}><span style={{display:"inline-flex",color:streak>0?C.warm:C.faint}}><Icon n="flame" s={15}/></span>{t("Стрик")}</div>
              <div style={tileValue}><CountUp value={streak}/> <span style={{fontSize:15,fontWeight:500,color:C.mid}}>{ruPlural(streak,"день","дня","дней")}</span></div>
              <div style={{fontSize:12.5,color:streak>0&&!streakInfo.studiedToday?C.warmDark:C.faint,marginTop:2}}>
                {streak>0&&!streakInfo.studiedToday?t("позанимайтесь сегодня"):t("рекорд {0}",[streakInfo.best])}
              </div>
            </div>
          </div>

          {/* Продолжить слушать */}
          {last&&(
            <ListRow divider={false} strong onClick={()=>onOpen(last.p)} tip={t("Открыть плеер")} chevron data-panel="" style={{...listBox,minHeight:64}}
              before={/* ▶ — играть здесь же (мини-плеер); нажатие на остальную карточку — открыть плеер */
                <UiIconButton variant="filled" size={44} onClick={()=>{onPlayHere?onPlayHere(last.p):onOpen(last.p);}}
                  label={playingId===last.p.id?t("Пауза"):t("Играть здесь")} style={{borderRadius:"50%"}}>
                  <Icon n={playingId===last.p.id?"pause":"play"} s={16} style={playingId===last.p.id?undefined:{marginLeft:2}}/>
                </UiIconButton>}
              title={<>{t("Продолжить:")} {last.p.name}</>}
              subtitle={<>{nCards(last.p.cardCount||0)}{last.pos>0?t(" · остановились на {0}",[fmtClock(last.pos)]):""}</>}/>
          )}

          {/* Очередь: что заиграет дальше */}
          {queue.length>0&&onOpenQueue&&(()=>{
            const next=rawProjects.find(p=>String(p.id)===queue[0]);
            return(
              <ListRow divider={false} strong onClick={onOpenQueue} tip={t("Открыть очередь")} chevron data-panel="" style={{...listBox,minHeight:64}}
                leading={<span style={{width:44,height:44,borderRadius:"50%",background:C.surface,color:C.blueDark,display:"inline-flex",alignItems:"center",justifyContent:"center"}}><Icon n="list" s={18}/></span>}
                title={`${t("Очередь")} · ${queue.length}`}
                subtitle={next&&t("Далее: {0}",[next.name])}/>
            );
          })()}

          {/* Библиотека */}
          <div>
            <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:8,flexWrap:"wrap",marginBottom:8}}>
              <h2 style={{...label,margin:0}}>{t("Библиотека")}</h2>
              <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
                {queueChip}
                {ungrouped.length+rootTexts.length>1&&sortBar}
              </div>
            </div>
            {loading&&!projects.length&&!folders.length?skeleton:isEmpty?(
              <div style={{...listBox,textAlign:"center",padding:"40px 16px"}}>
                <div style={{color:C.blueDark,marginBottom:10}}><Icon n="headphones" s={32} sw={1.5}/></div>
                <div style={{fontSize:17,fontWeight:600,color:C.text,marginBottom:6}}>{t("Добро пожаловать!")}</div>
                <div style={{fontSize:14,color:C.mid,marginBottom:18,lineHeight:1.55}}>{t("Загрузите аудио и добавляйте карточки прямо во время прослушивания — или начните с текста.")}</div>
                <div style={{display:"flex",gap:8,justifyContent:"center",flexWrap:"wrap"}}>
                  <UiButton variant="primary" icon="headphones" onClick={()=>onNew(null)}>{t("Аудио проект")}</UiButton>
                  <UiButton icon="book" onClick={()=>onNewTextProject()}>{t("Текст")}</UiButton>
                </div>
              </div>
            ):(
              <div className={libClass} data-panel="" style={libBox}>
                {folders.map(folderRow)}
                {sortedRows(ungrouped,rootTexts)}
              </div>
            )}
          </div>
          {/* «Аркада» на телефоне: бокового меню нет — автомат со словами внизу главной */}
          {navMode&&C.face&&C.lift&&slotWords?.length>0&&<WordSlots C={C} words={slotWords} style={{width:"100%",maxWidth:360,margin:"8px auto 0",boxSizing:"border-box"}}/>}
        </>
        )}
      </div>
      {extras}
    </div>
  );
}
